/** Home Customize Layout — drag, resize, center snap, persist. */

import { touchAccountSettingsUpdatedAt } from "./account-sync";

export type HomeLayoutItem = { x: number; y: number; w: number; h: number };
export type HomeLayoutMap = Record<string, HomeLayoutItem>;

export const HOME_LAYOUT_WIDGET_IDS = [
  "start",
  "friends",
  "lastMode",
  "trending",
  "dots",
  "streak",
  "challenge",
  "lootdrops",
  "newInShop",
  "suggested",
  "continuePlaying",
] as const;

export type HomeLayoutWidgetId = (typeof HOME_LAYOUT_WIDGET_IDS)[number];

const LAYOUT_KEY = "settingHomeLayoutV1";
const SNAP_PX = 14;

export const DEFAULT_HOME_LAYOUT: HomeLayoutMap = {
  start: { x: -1, y: -1, w: 260, h: 64 },
  friends: { x: 24, y: 120, w: 200, h: 160 },
  lastMode: { x: 24, y: 300, w: 200, h: 72 },
  trending: { x: 24, y: 390, w: 200, h: 72 },
  dots: { x: 24, y: 480, w: 160, h: 56 },
  streak: { x: -1, y: -1, w: 64, h: 64 },
  challenge: { x: 240, y: 120, w: 140, h: 140 },
  lootdrops: { x: 240, y: 280, w: 220, h: 100 },
  newInShop: { x: 240, y: 400, w: 260, h: 110 },
  suggested: { x: 500, y: 120, w: 200, h: 88 },
  continuePlaying: { x: 500, y: 230, w: 220, h: 72 },
};

export type HomeLayoutCustomizeOpts = {
  getRoot: () => HTMLElement | null;
  getWidgetEl: (id: string) => HTMLElement | null;
  getMovableIds: () => string[];
  onChanged: () => void;
  isTouchUi: () => boolean;
};

let opts: HomeLayoutCustomizeOpts | null = null;
let editing = false;
let selectedId: string | null = null;
let snapDisabled = false;
let shiftHeld = false;
let dragState: null | {
  id: string;
  mode: "move" | "resize";
  startX: number;
  startY: number;
  orig: HomeLayoutItem;
} = null;

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

export function readHomeLayout(): HomeLayoutMap {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return { ...DEFAULT_HOME_LAYOUT };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { ...DEFAULT_HOME_LAYOUT };
    const out: HomeLayoutMap = { ...DEFAULT_HOME_LAYOUT };
    for (const id of HOME_LAYOUT_WIDGET_IDS) {
      const row = (parsed as Record<string, unknown>)[id];
      if (!row || typeof row !== "object") continue;
      const o = row as Record<string, unknown>;
      const x = Number(o.x);
      const y = Number(o.y);
      const w = Number(o.w);
      const h = Number(o.h);
      if (![x, y, w, h].every((n) => Number.isFinite(n))) continue;
      out[id] = { x, y, w: Math.max(48, w), h: Math.max(36, h) };
    }
    return out;
  } catch {
    return { ...DEFAULT_HOME_LAYOUT };
  }
}

export function writeHomeLayout(map: HomeLayoutMap): void {
  try {
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(map));
    touchAccountSettingsUpdatedAt();
  } catch {
    /* ignore */
  }
}

export function resetHomeLayout(): void {
  writeHomeLayout({ ...DEFAULT_HOME_LAYOUT });
}

export function applyLayoutToElement(el: HTMLElement, item: HomeLayoutItem, id: string): void {
  const useDefaultStart = id === "start" && item.x < 0 && item.y < 0;
  if (id === "start") {
    if (useDefaultStart) {
      el.classList.remove("homeWidget--laidOut");
      el.style.removeProperty("--home-w-left");
      el.style.removeProperty("--home-w-top");
      el.style.removeProperty("--home-w-width");
      el.style.removeProperty("--home-w-height");
      return;
    }
    el.classList.add("homeWidget--laidOut");
    el.style.setProperty("--home-w-left", `${item.x}px`);
    el.style.setProperty("--home-w-top", `${item.y}px`);
    el.style.setProperty("--home-w-width", `${item.w}px`);
    el.style.setProperty("--home-w-height", `${item.h}px`);
    return;
  }
  if (id === "streak" && item.x < 0 && item.y < 0) {
    el.style.left = "";
    el.style.top = "";
    el.style.width = "";
    el.style.height = "";
    el.classList.remove("homeWidget--laidOut");
    return;
  }
  el.classList.add("homeWidget--laidOut");
  el.style.left = `${item.x}px`;
  el.style.top = `${item.y}px`;
  el.style.width = `${item.w}px`;
  el.style.height = `${item.h}px`;
}

function ensureEditChrome() {
  let bar = document.getElementById("homeLayoutEditBar");
  if (!bar) {
    bar = document.createElement("div");
    bar.id = "homeLayoutEditBar";
    bar.className = "homeLayoutEditBar hidden";
    bar.innerHTML = `
      <span>Layout</span>
      <label>X <input type="number" id="homeLayoutPosX" step="1"></label>
      <label>Y <input type="number" id="homeLayoutPosY" step="1"></label>
      <label>W <input type="number" id="homeLayoutSizeW" step="1" min="48"></label>
      <label>H <input type="number" id="homeLayoutSizeH" step="1" min="36"></label>
      <button type="button" class="btn" id="homeLayoutDoneBtn">Done</button>
      <button type="button" class="btn" id="homeLayoutResetBtn">Reset Layout</button>
    `;
    document.body.appendChild(bar);
    bar.querySelector("#homeLayoutDoneBtn")?.addEventListener("click", () => exitHomeLayoutEdit());
    bar.querySelector("#homeLayoutResetBtn")?.addEventListener("click", () => {
      resetHomeLayout();
      applyAllLayouts();
      refreshLayoutEditBar();
      opts?.onChanged();
    });
    for (const id of ["homeLayoutPosX", "homeLayoutPosY", "homeLayoutSizeW", "homeLayoutSizeH"]) {
      const input = bar.querySelector(`#${id}`) as HTMLInputElement | null;
      input?.addEventListener("change", () => commitBarInputs());
    }
  }
  let snapBtn = document.getElementById("homeLayoutSnapToggle") as HTMLButtonElement | null;
  if (!snapBtn) {
    snapBtn = document.createElement("button");
    snapBtn.type = "button";
    snapBtn.id = "homeLayoutSnapToggle";
    snapBtn.className = "homeLayoutSnapToggle";
    snapBtn.title = "Disable snapping";
    snapBtn.setAttribute("aria-label", "Disable layout snapping");
    snapBtn.textContent = "SNAP";
    document.body.appendChild(snapBtn);
    snapBtn.addEventListener("click", () => {
      snapDisabled = !snapDisabled;
      snapBtn!.classList.toggle("homeLayoutSnapToggle--on", snapDisabled);
      snapBtn!.title = snapDisabled ? "Snapping off (free move)" : "Snapping on";
    });
  }
  for (const kind of ["v", "h"] as const) {
    const gid = kind === "v" ? "homeLayoutSnapGuideV" : "homeLayoutSnapGuideH";
    if (!document.getElementById(gid)) {
      const g = document.createElement("div");
      g.id = gid;
      g.className = `homeLayoutSnapGuide homeLayoutSnapGuide--${kind} hidden`;
      document.body.appendChild(g);
    }
  }
}

function showGuides(v: boolean, h: boolean) {
  document.getElementById("homeLayoutSnapGuideV")?.classList.toggle("hidden", !v);
  document.getElementById("homeLayoutSnapGuideH")?.classList.toggle("hidden", !h);
}

function snapEnabledNow(): boolean {
  if (snapDisabled) return false;
  if (shiftHeld) return false;
  return true;
}

function applySnap(item: HomeLayoutItem): { item: HomeLayoutItem; gv: boolean; gh: boolean } {
  if (!snapEnabledNow()) return { item, gv: false, gh: false };
  const cx = item.x + item.w / 2;
  const cy = item.y + item.h / 2;
  const midX = window.innerWidth / 2;
  const midY = window.innerHeight / 2;
  let gv = false;
  let gh = false;
  const next = { ...item };
  if (Math.abs(cx - midX) <= SNAP_PX) {
    next.x = midX - item.w / 2;
    gv = true;
  }
  if (Math.abs(cy - midY) <= SNAP_PX) {
    next.y = midY - item.h / 2;
    gh = true;
  }
  return { item: next, gv, gh };
}

function currentItemFor(id: string): HomeLayoutItem {
  const map = readHomeLayout();
  const base = map[id] || DEFAULT_HOME_LAYOUT[id] || { x: 40, y: 40, w: 180, h: 80 };
  if (id === "start" && base.x < 0) {
    const el = opts?.getWidgetEl("start");
    if (el) {
      const r = el.getBoundingClientRect();
      return { x: r.left, y: r.top, w: r.width, h: r.height };
    }
  }
  return { ...base };
}

function persistItem(id: string, item: HomeLayoutItem) {
  const map = readHomeLayout();
  map[id] = item;
  writeHomeLayout(map);
  const el = opts?.getWidgetEl(id);
  if (el) applyLayoutToElement(el, item, id);
  opts?.onChanged();
}

function commitBarInputs() {
  if (!selectedId) return;
  const x = Number((document.getElementById("homeLayoutPosX") as HTMLInputElement | null)?.value);
  const y = Number((document.getElementById("homeLayoutPosY") as HTMLInputElement | null)?.value);
  const w = Number((document.getElementById("homeLayoutSizeW") as HTMLInputElement | null)?.value);
  const h = Number((document.getElementById("homeLayoutSizeH") as HTMLInputElement | null)?.value);
  if (![x, y, w, h].every((n) => Number.isFinite(n))) return;
  persistItem(selectedId, {
    x,
    y,
    w: clamp(w, 48, window.innerWidth),
    h: clamp(h, 36, window.innerHeight),
  });
}

export function refreshLayoutEditBar() {
  const bar = document.getElementById("homeLayoutEditBar");
  if (!bar || !selectedId) return;
  const item = currentItemFor(selectedId);
  const set = (id: string, v: number) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el && document.activeElement !== el) el.value = String(Math.round(v));
  };
  set("homeLayoutPosX", item.x);
  set("homeLayoutPosY", item.y);
  set("homeLayoutSizeW", item.w);
  set("homeLayoutSizeH", item.h);
}

function ensureResizeHandle(el: HTMLElement) {
  if (el.querySelector(".homeWidgetResizeHandle")) return;
  const h = document.createElement("div");
  h.className = "homeWidgetResizeHandle";
  h.dataset.homeResize = "1";
  el.appendChild(h);
}

function clearSelection() {
  document.querySelectorAll(".homeWidget--selected").forEach((n) => n.classList.remove("homeWidget--selected"));
  selectedId = null;
}

function selectWidget(id: string) {
  clearSelection();
  selectedId = id;
  const el = opts?.getWidgetEl(id);
  if (el) {
    el.classList.add("homeWidget--selected");
    ensureResizeHandle(el);
  }
  refreshLayoutEditBar();
}

function applyAllLayouts() {
  if (!opts) return;
  const map = readHomeLayout();
  for (const id of opts.getMovableIds()) {
    const el = opts.getWidgetEl(id);
    const item = map[id] || DEFAULT_HOME_LAYOUT[id];
    if (!el || !item) continue;
    applyLayoutToElement(el, item, id);
    if (editing) ensureResizeHandle(el);
  }
}

function onPointerDown(e: PointerEvent) {
  if (!editing || !opts) return;
  const t = e.target as HTMLElement | null;
  if (!t) return;
  if (t.closest("#homeLayoutEditBar") || t.closest("#homeLayoutSnapToggle")) return;
  const resize = t.closest(".homeWidgetResizeHandle") as HTMLElement | null;
  const widget = (resize || t).closest("[data-home-widget]") as HTMLElement | null;
  if (!widget) {
    clearSelection();
    return;
  }
  const id = widget.getAttribute("data-home-widget");
  if (!id || !opts.getMovableIds().includes(id)) return;
  e.preventDefault();
  e.stopPropagation();
  selectWidget(id);
  const orig = currentItemFor(id);
  dragState = {
    id,
    mode: resize ? "resize" : "move",
    startX: e.clientX,
    startY: e.clientY,
    orig,
  };
  widget.classList.add("homeWidget--dragging");
  widget.setPointerCapture?.(e.pointerId);
}

function onPointerMove(e: PointerEvent) {
  if (!dragState || !opts) return;
  const dx = e.clientX - dragState.startX;
  const dy = e.clientY - dragState.startY;
  let next: HomeLayoutItem;
  if (dragState.mode === "resize") {
    next = {
      ...dragState.orig,
      w: clamp(dragState.orig.w + dx, 48, window.innerWidth),
      h: clamp(dragState.orig.h + dy, 36, window.innerHeight),
    };
    showGuides(false, false);
  } else {
    next = {
      ...dragState.orig,
      x: dragState.orig.x + dx,
      y: dragState.orig.y + dy,
    };
    const snapped = applySnap(next);
    next = snapped.item;
    showGuides(snapped.gv, snapped.gh);
  }
  const el = opts.getWidgetEl(dragState.id);
  if (el) applyLayoutToElement(el, next, dragState.id);
  if (selectedId === dragState.id) {
    const set = (id: string, v: number) => {
      const input = document.getElementById(id) as HTMLInputElement | null;
      if (input) input.value = String(Math.round(v));
    };
    set("homeLayoutPosX", next.x);
    set("homeLayoutPosY", next.y);
    set("homeLayoutSizeW", next.w);
    set("homeLayoutSizeH", next.h);
  }
  (dragState as { _live?: HomeLayoutItem })._live = next;
}

function onPointerUp(e: PointerEvent) {
  if (!dragState) return;
  const id = dragState.id;
  const live = (dragState as { _live?: HomeLayoutItem })._live || currentItemFor(id);
  const el = opts?.getWidgetEl(id);
  el?.classList.remove("homeWidget--dragging");
  persistItem(id, live);
  dragState = null;
  showGuides(false, false);
  try {
    (e.target as HTMLElement)?.releasePointerCapture?.(e.pointerId);
  } catch {
    /* ignore */
  }
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key === "Shift") shiftHeld = true;
}

function onKeyUp(e: KeyboardEvent) {
  if (e.key === "Shift") {
    shiftHeld = false;
    showGuides(false, false);
  }
}

function onStartClickCapture(e: Event) {
  if (!editing) return;
  const t = e.target as HTMLElement | null;
  if (t?.closest("#openModes") || t?.closest('[data-home-widget="start"]')) {
    e.preventDefault();
    e.stopPropagation();
  }
}

export function mountHomeLayoutCustomize(o: HomeLayoutCustomizeOpts) {
  opts = o;
  ensureEditChrome();
  if ((document.body as HTMLElement & { dataset?: DOMStringMap }).dataset?.homeLayoutMounted === "1") return;
  document.body.dataset.homeLayoutMounted = "1";
  window.addEventListener("pointerdown", onPointerDown, true);
  window.addEventListener("pointermove", onPointerMove, true);
  window.addEventListener("pointerup", onPointerUp, true);
  window.addEventListener("pointercancel", onPointerUp, true);
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  document.addEventListener("click", onStartClickCapture, true);
}

export function enterHomeLayoutEdit() {
  ensureEditChrome();
  editing = true;
  snapDisabled = false;
  document.body.classList.add("home-layout-editing");
  document.body.classList.toggle("home-layout-touch", !!opts?.isTouchUi());
  document.getElementById("homeLayoutEditBar")?.classList.remove("hidden");
  const snapBtn = document.getElementById("homeLayoutSnapToggle");
  snapBtn?.classList.remove("homeLayoutSnapToggle--on");
  applyAllLayouts();
  const ids = opts?.getMovableIds() || [];
  if (ids.length) selectWidget(ids.includes("start") ? "start" : ids[0]!);
}

export function exitHomeLayoutEdit() {
  editing = false;
  dragState = null;
  clearSelection();
  showGuides(false, false);
  document.body.classList.remove("home-layout-editing", "home-layout-touch");
  document.getElementById("homeLayoutEditBar")?.classList.add("hidden");
  document.querySelectorAll(".homeWidgetResizeHandle").forEach((n) => n.remove());
}

export function isHomeLayoutEditing(): boolean {
  return editing;
}

export function syncHomeWidgetLayouts(movableIds: string[]) {
  if (!opts) return;
  const map = readHomeLayout();
  for (const id of movableIds) {
    const el = opts.getWidgetEl(id);
    const item = map[id] || DEFAULT_HOME_LAYOUT[id];
    if (!el || !item) continue;
    applyLayoutToElement(el, item, id);
  }
}
